import { useEffect, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';

import { ACTIONS, clientReducer } from './clientReducer';
import { initialUpdateClientState } from './clientInitialStates';
import ClientForm from './clientForm';
import DuplicateWarningModal from './DuplicateWarningModal';
import { errorNormalizer } from '../../../shared/Helpers/functions';
import CustomAlert from '../../../shared/uiComponents/Alert';
import { CustomButton, Text } from '../../../shared/uiComponents';
import { TEXT } from '../../../shared/Helpers/colors';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getClient, updateClient } from '../../../redux/State/clientSlice/clientSlice';
import { getUserBranches } from '../../../redux/State/userSlice';
import {
	DuplicateMatchProperties,
	UpdateClientInfoProperties,
} from '../../../redux/API/ClientAPIHelpers/clientProperties';

const UpdateClient = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const { clientId } = useParams();

	const currentUserId = useSelector((state) => state.account.user.id);
	const userBranches = useSelector((state) => state.userBranch.userBranches);
	const statuses = useSelector((state) => state.client.statuses);

	const [clientInfo, setClientInfo] = useReducer(clientReducer, initialUpdateClientState);
	const [duplicates, setDuplicates] = useState<DuplicateMatchProperties[]>([]);
	const [showDuplicateWarning, setShowDuplicateWarning] = useState<boolean>(false);
	const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
	const [originalStatus, setOriginalStatus] = useState<number | null>(null);
	const [showPauseConfirm, setShowPauseConfirm] = useState(false);

	const pausedStatusId = statuses?.find((s) => s.name === 'Paused')?.id as number | undefined;

	useEffect(() => {
		if (!currentUserId) return;
		dispatch(getUserBranches(currentUserId));
	}, [currentUserId, dispatch]);

	useEffect(() => {
		if (!clientId) return;
		dispatch(getClient(clientId))
			.then(unwrapResult)
			.then((response) => {
				const { status, diagnosisCode, caseCoordinator, branches, insurances, isElc } = response;
				const diagnosisCodeId = diagnosisCode?.id || '';
				const caseCoordinatorId = caseCoordinator?.id || '';
				// Extract branchId only if it's explicitly assigned (not auto-assigned)
				const branchId = Boolean(branches?.length) && !branches[0].isAutoAssigned ? branches[0].id : '';
				const payload: UpdateClientInfoProperties = {
					...response,
					status: status.id,
					diagnosisCodeId,
					caseCoordinatorId,
					branchId,
					insurances: insurances || [], // Explicitly preserve insurances
					isElc: Boolean(isElc),
				};

				setClientInfo({ type: ACTIONS.setState, payload });
				setOriginalStatus(status.id);
			})
			.catch(errorNormalizer);
	}, [clientId, dispatch]);

	const getClientData = () => ({
		...clientInfo,
		firstName: clientInfo.firstName.trim(),
		lastName: clientInfo.lastName.trim(),
	});

	const navigateAfterUpdate = (response: any) => {
		const updatedClientBranches = response?.branches || [];
		const hasAccess =
			!updatedClientBranches.length ||
			updatedClientBranches.some((clientBranch) =>
				userBranches.some((userBranch) => userBranch.id === clientBranch.id),
			);

		if (hasAccess) {
			navigate(-1);
		} else {
			navigate('/clients');
		}
	};

	const saveClient = () => {
		dispatch(updateClient(getClientData()))
			.then(unwrapResult)
			.then(navigateAfterUpdate)
			.catch((error) => {
				if (error?.duplicates) {
					setDuplicates(error.duplicates);
					setShowDuplicateWarning(true);

					return;
				}

				if (error?.errors) {
					setServerErrors(error.errors);
				}
				errorNormalizer(error);
			});
	};

	const updateClientHandler = () => {
		const isNewlyPaused =
			pausedStatusId !== undefined &&
			originalStatus !== pausedStatusId &&
			clientInfo.status === pausedStatusId;

		if (isNewlyPaused) {
			setShowPauseConfirm(true);
		} else {
			saveClient();
		}
	};

	const onConfirmPause = () => {
		setShowPauseConfirm(false);
		saveClient();
	};

	const onConfirmDuplicate = () => {
		setShowDuplicateWarning(false);
		dispatch(updateClient({ ...getClientData(), acknowledgeDuplicates: true }))
			.then(unwrapResult)
			.then(navigateAfterUpdate)
			.catch(errorNormalizer);
	};

	const onCancelDuplicate = () => {
		setShowDuplicateWarning(false);
		setDuplicates([]);
	};

	return (
		<>
			<ClientForm
				clientInfo={clientInfo}
				setClientInfo={setClientInfo}
				saveClientHandler={updateClientHandler}
				duplicateWarnings={duplicates}
				serverErrors={serverErrors}
			/>
			<DuplicateWarningModal
				open={showDuplicateWarning}
				duplicates={duplicates}
				onConfirm={onConfirmDuplicate}
				onCancel={onCancelDuplicate}
			/>
			<CustomAlert
				open={showPauseConfirm}
				onClose={() => setShowPauseConfirm(false)}
				title="End Schedules"
				maxWidth="xs"
				Content={() => (
					<div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
						<Text
							title="Pausing this client will end all active schedules as of today."
							type="psm"
							color={TEXT.SECONDARY}
						/>
						<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
							<CustomButton title="Cancel" buttonType="secondary" onClick={() => setShowPauseConfirm(false)} />
							<CustomButton title="Continue" buttonType="primary" onClick={onConfirmPause} />
						</div>
					</div>
				)}
			/>
		</>
	);
};

export default UpdateClient;
